import React, { useEffect, useReducer, useCallback, useContext } from 'react';
import { axios } from '../..';
import jwtDecode from 'jwt-decode';
import css from './LoginForm.module.css';
import classNames from 'classnames';
import AuthContext from '../../store/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import EyeOpen from '../Atomos/Icons/EyeOpen';
import EyeClosed from '../Atomos/Icons/EyeClosed';
// EventBus y EventBusValues se mantienen para uso futuro, pero actualmente no se usan

import ssogoogle from '../../assets/images&icons/Google-Vector.svg';
import ssolinkedin from '../../assets/images&icons/LinkedIn-Vector.svg';
import { LoginWithGoogle } from '../Auth/Providers/Google';
import { LoginWithLinkedIn } from '../Auth/Providers/LinkedIn';
import { getCookie } from '../../utils/cookies';
import { generateLegacyCookies, initializeLegacySessions } from '../../utils/legacySession';
import { getAndClearRedirectPath } from '../../utils/logout';
import { clearApplicationCache, redirectAfterLogin } from '../../utils/clearApplicationCache';
import NewButton from '../Atomos/Buttons/newButton';
import { resolveAuthErrorPayload } from '../../utils/authErrorPayload';

type State = {
  checked: boolean;
  userName: string;
  password: string;
  errorMessage: string;
  token: string;
  bgLoading: boolean;
  loginInProgress: boolean;
  googleLinkMessage: string;
};

type Action =
  | { type: 'SET_CHECKED'; payload: boolean }
  | { type: 'SET_USER_NAME'; payload: string }
  | { type: 'SET_PASSWORD'; payload: string }
  | { type: 'SET_ERROR_MESSAGE'; payload: string }
  | { type: 'SET_TOKEN'; payload: string }
  | { type: 'SET_BG_LOADING'; payload: boolean }
  | { type: 'SET_LOGIN_IN_PROGRESS'; payload: boolean }
  | { type: 'SET_GOOGLE_LINK_MESSAGE'; payload: string };


const initialState: State = {
  checked: false,
  userName: '',
  password: '',
  errorMessage: '',
  token: '',
  bgLoading: false,
  loginInProgress: false,
  googleLinkMessage: '',
};
 
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_CHECKED':
      return { ...state, checked: action.payload };
    case 'SET_USER_NAME':
      return { ...state, userName: action.payload };
    case 'SET_PASSWORD':
      return { ...state, password: action.payload };
    case 'SET_ERROR_MESSAGE':
      return { ...state, errorMessage: action.payload };
    case 'SET_TOKEN':
      return { ...state, token: action.payload };
    case 'SET_BG_LOADING':
      return { ...state, bgLoading: action.payload };
    case 'SET_LOGIN_IN_PROGRESS':
      return { ...state, loginInProgress: action.payload };
    case 'SET_GOOGLE_LINK_MESSAGE':
      return { ...state, googleLinkMessage: action.payload };
    default:
      return state;
  }
};

const LoginForm = () => {
  const { setIsAuthenticated, loggedPerson, setAuthTransitionActive } = useContext(AuthContext);
  const [state, dispatchLocal] = useReducer(reducer, initialState);
  const [redirectUrl, setRedirectUrl] = React.useState<string>(''); 
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const hasExecutedAfterLogin = React.useRef(false);
  const sessionsInitializedRef = React.useRef(false);
  const iframeLoadingRef = React.useRef(false);
  const handleCheckboxChange = useCallback(() => {
    dispatchLocal({ type: 'SET_CHECKED', payload: !state.checked });
  }, [state.checked]);

  const setCookie = useCallback((name: string, value: string, days: number) => {
		if(days === 0) {
			document.cookie = `${name}=${value};path=/`;
			return;
		}

    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "expires=" + date.toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/";
  }, []);

  useEffect(function setupGoogleListeners() {
    const handleSocialAccountLinkingRequired = (event: any) => {
      const socialProvider = event.detail?.socialProvider;
      navigate('/link-account-login', { state: { socialProvider } });
    };

    window.addEventListener('socialAccountLinkingRequired', handleSocialAccountLinkingRequired);
    
    return () => {
      window.removeEventListener('socialAccountLinkingRequired', handleSocialAccountLinkingRequired);
    };
  }, [navigate, setCookie]);


  const handleAfterLogin = useCallback(async () => {
    if (!loggedPerson) {
      return;
    }
    if (getCookie("ssoLogin") === "true") {
      return;
    }
    
    const hasExecutedKey = `hasExecutedAfterLogin_${loggedPerson?.userId || 'unknown'}`;
    if (hasExecutedAfterLogin.current || sessionStorage.getItem(hasExecutedKey)) {
      return;
    }
    
    hasExecutedAfterLogin.current = true;
    sessionStorage.setItem(hasExecutedKey, 'true');
    
    if (loggedPerson?.twoFactorAuthentication === undefined) {
      navigate('/two-factor-acknowledgement', { replace: true });
      return;
    }

    const query = window.location.search;
    const equalIndex = query.indexOf('=');
    let legacyUrl: string | null = null;
    if (equalIndex !== -1) {
      const candidateUrl = query.substring(equalIndex + 1);
      if (candidateUrl.toLowerCase().includes('.asp')) {
        legacyUrl = candidateUrl;
      }
    }

    if (legacyUrl) {
      try {
        await generateLegacyCookies();
      } catch (error) {
        console.error('[handleAfterLogin] ERROR:', error);
      }

      const token = getCookie("token");
      if (token && !legacyUrl.includes('token=')) {
        const separator = legacyUrl.includes('?') ? '&' : '?';
        window.location.href = `${legacyUrl}${separator}token=${encodeURIComponent(token)}`;
      } else {
        window.location.href = legacyUrl;
      }
      return;
    }

    generateLegacyCookies().catch((error) => {
      console.error('[handleAfterLogin] ERROR:', error);
    });

    const redirectPath = getAndClearRedirectPath();
      
    if (redirectPath) {
      redirectAfterLogin(redirectPath);
    } else {
      const currentPath = window.location.pathname;
      const authRoutes = ['/login', '/two-factor-auth', '/two-factor-acknowledgement', '/link-account-login', '/', '/login.asp'];
      const isAuthRoute = authRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'));
      
      if (isAuthRoute) {
        redirectAfterLogin('/mywsg');
      }
    }
  }, [pathname, loggedPerson, navigate]);

  const completeLogin = useCallback(async (loginData: any, rememberMe?: boolean, username?: string) => {
    await clearApplicationCache();

    const id_token = (loginData.id_token as string).toLowerCase();
    const ExclusiveWords = ['end', 'sql', 'drop'];
    if(ExclusiveWords.some((word) => id_token.includes(word))) {
      // Retry login if token contains exclusive words
      return;
    }

    const shouldRemember = rememberMe !== undefined ? rememberMe : state.checked;
    if (shouldRemember) {
      setCookie("token", loginData.id_token, 300);
      setCookie("token_decoded", JSON.stringify(jwtDecode(loginData.id_token)), 300);
    } else {
      setCookie("token", loginData.id_token, 0);
      setCookie("token_decoded", JSON.stringify(jwtDecode(loginData.id_token)), 0);
    }

    if (loginData.refresh_token) {
      sessionStorage.setItem("refresh_token", loginData.refresh_token);
    }
    if (loginData.access_token) {
      sessionStorage.setItem("access_token", loginData.access_token);
    }
    sessionStorage.setItem("access_timestamp", Date.now().toString());

    const claims = jwtDecode(loginData.id_token) as any;
    sessionStorage.setItem("token_decoded", JSON.stringify(claims));
    
    // Store username if provided (for regular login) or extract from token (for Google SSO)
    if (username) {
      sessionStorage.setItem("username", username);
      setCookie("username", username, 0);
    }    
    dispatchLocal({ type: 'SET_TOKEN', payload: loginData.id_token });
    setIsAuthenticated(true);
    
    // Clean up pending login data
    sessionStorage.removeItem("pendingLogin");
    
    // Reset the guard so handleAfterLogin can execute for this new login
    hasExecutedAfterLogin.current = false;
    
    const userId = (claims as any)?.oid || (claims as any)?.sub || 'unknown';
    sessionStorage.removeItem(`hasExecutedAfterLogin_${userId}`);
    
    // handleAfterLogin will be called by useEffect when loggedPerson becomes available
  }, [state.checked, setIsAuthenticated, setCookie]);

  const handleLogin = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      if (state.userName.trim() === '') {
        throw new Error("Please enter a username.");
      }

      if (state.password.trim() === '') {
        throw new Error("Please enter a password.");
      }

      dispatchLocal({ type: 'SET_LOGIN_IN_PROGRESS', payload: true });
      dispatchLocal({ type: 'SET_BG_LOADING', payload: true });
      setAuthTransitionActive(true);

      const response = await axios.Post("/login", {
        username: state.userName,
        password: state.password
      });
      if (response && response.data) {
        if (response.data.twoFactorAuthenticationNeeded) {
          sessionStorage.setItem("username", state.userName);
          setCookie("username", state.userName, 0);
          sessionStorage.setItem("pendingLogin", JSON.stringify({
            username: state.userName,
            password: state.password,
            rememberMe: state.checked
          }));
          navigate('/two-factor-auth', { replace: true });
          return;
        }
        await completeLogin(response.data, undefined, state.userName);
      }
    } catch (error: any) {
        const rawData = error.response?.data ?? error.message;
        const resolved = resolveAuthErrorPayload(rawData);
        let errorMessage = resolved.message || String(rawData ?? error.message);
        if (
          error?.response?.status === 403 &&
          !resolved.isFirmLoginNotAllowed &&
          !resolved.isAccountDisabled
        ) {
          setRedirectUrl('/modify-password')
          errorMessage = "The user's password has expired. To reset password click "
        } else {
          setRedirectUrl('')
        }
      dispatchLocal({ type: 'SET_LOGIN_IN_PROGRESS', payload: false });
      dispatchLocal({ type: 'SET_BG_LOADING', payload: false });
      setAuthTransitionActive(false);
      dispatchLocal({ type: 'SET_ERROR_MESSAGE', payload: String(errorMessage) });
    }
  }, [state.userName, state.password, state.checked, axios, navigate, completeLogin, setCookie, setAuthTransitionActive]);

  useEffect(() => {
    if (loggedPerson) {
      handleAfterLogin();
    }
  }, [loggedPerson, handleAfterLogin]);

  useEffect(() => {
    let lastToken: string | null = null;
    let initTimer: NodeJS.Timeout | null = null;

    const checkAndInitializeSessions = async () => {
      const token = getCookie("token");
      
      if (!token) {
        if (lastToken) {
          sessionsInitializedRef.current = false;
          lastToken = null;
          sessionStorage.removeItem('legacySessionsInitialized');
        }
        return;
      }

      if (lastToken && lastToken !== token) {
        sessionsInitializedRef.current = false;
        lastToken = token;
      } else if (!lastToken) {
        lastToken = token;
      }

      if (sessionsInitializedRef.current) {
        return;
      }

      const sessionsInitKey = 'legacySessionsInitialized';
      const lastInitTime = sessionStorage.getItem(sessionsInitKey);
      const now = Date.now();
      
      if (lastInitTime && (now - parseInt(lastInitTime)) < 30000) {
        sessionsInitializedRef.current = true;
        return;
      }
      
      try {
        await initializeLegacySessions(iframeLoadingRef, 0);
        sessionStorage.setItem(sessionsInitKey, now.toString());
        sessionsInitializedRef.current = true;
      } catch (error) {
        console.error('[TokenObserver] ERROR:', error)
      }
    };

    checkAndInitializeSessions();
    
    initTimer = setInterval(() => {
      checkAndInitializeSessions();
    }, 2000);

    return () => {
      if (initTimer) {
        clearInterval(initTimer);
      }
    };
  }, []);


  useEffect(() => {
    const handleGoogleLoginSuccess = async (event: any) => {
      try {
        const { tokens } = event.detail;
        
        if (tokens && tokens.id_token) {
          dispatchLocal({ type: 'SET_ERROR_MESSAGE', payload: '' });
          dispatchLocal({ type: 'SET_LOGIN_IN_PROGRESS', payload: true });
          setAuthTransitionActive(true);
          await completeLogin(tokens, false);
        } else {
          throw new Error('No valid tokens received from B2C Google authentication');
        }
      } catch (error: any) {
        console.error('Google login error:', error);
        dispatchLocal({ type: 'SET_LOGIN_IN_PROGRESS', payload: false });
        setAuthTransitionActive(false);
        dispatchLocal({
          type: 'SET_ERROR_MESSAGE',
          payload: error.message || 'Google login failed. Please try again.'
        });
      }
    };

    window.addEventListener('googleLoginSuccess', handleGoogleLoginSuccess);
    const handleGoogleLoginError = (event: any) => {
      const resolved = resolveAuthErrorPayload(
        event?.detail?.code
          ? { code: event.detail.code, message: event.detail.message }
          : event?.detail?.message,
      );
      dispatchLocal({ type: 'SET_LOGIN_IN_PROGRESS', payload: false });
      setAuthTransitionActive(false);
      dispatchLocal({
        type: 'SET_ERROR_MESSAGE',
        payload:
          resolved.message ||
          event?.detail?.message ||
          'Google login failed. Please try again.',
      });
    };
    window.addEventListener('googleLoginError', handleGoogleLoginError);

    return () => {
      window.removeEventListener('googleLoginSuccess', handleGoogleLoginSuccess);
      window.removeEventListener('googleLoginError', handleGoogleLoginError);
    };
  }, [completeLogin, setAuthTransitionActive]);

    useEffect(() => {
      const handleLinkedInLoginSuccess = async (event: any) => {
        try {
          const { tokens } = event.detail;
          
          if (tokens && tokens.id_token) {
            dispatchLocal({ type: 'SET_ERROR_MESSAGE', payload: '' });
            dispatchLocal({ type: 'SET_LOGIN_IN_PROGRESS', payload: true });
            setAuthTransitionActive(true);
            await completeLogin(tokens, false);
          } else {
            throw new Error('No valid tokens received from B2C LinkedIn authentication');
          }
        } catch (error: any) {
          console.error('LinkedIn login error:', error);
          dispatchLocal({ type: 'SET_LOGIN_IN_PROGRESS', payload: false });
          setAuthTransitionActive(false);
          dispatchLocal({
            type: 'SET_ERROR_MESSAGE',
            payload: error.message || 'LinkedIn login failed. Please try again.'
          });
        }
      };
  
      window.addEventListener('linkedinLoginSuccess', handleLinkedInLoginSuccess);
      const handleLinkedInLoginError = (event: any) => {
        const resolved = resolveAuthErrorPayload(
          event?.detail?.code
            ? { code: event.detail.code, message: event.detail.message }
            : event?.detail?.message,
        );
        dispatchLocal({ type: 'SET_LOGIN_IN_PROGRESS', payload: false });
        setAuthTransitionActive(false);
        dispatchLocal({
          type: 'SET_ERROR_MESSAGE',
          payload:
            resolved.message ||
            event?.detail?.message ||
            'LinkedIn login failed. Please try again.',
        });
      };
      window.addEventListener('linkedinLoginError', handleLinkedInLoginError);

      return () => {
        window.removeEventListener('linkedinLoginSuccess', handleLinkedInLoginSuccess);
        window.removeEventListener('linkedinLoginError', handleLinkedInLoginError);
      };
    }, [completeLogin, setAuthTransitionActive]);
  

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_gl')) {
      url.searchParams.delete('_gl');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);
  
  useEffect(() => {
    dispatchLocal({ type: 'SET_BG_LOADING', payload: false });

		const urlParams = new URLSearchParams(window.location.search);
		const error = urlParams.get('error');
		if(error) {
			dispatchLocal({ type: 'SET_ERROR_MESSAGE', payload: String(error) });
		}
  }, []);

  useEffect(() => {
    const form = formRef.current;
    if (!form) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || state.loginInProgress) {
        return;
      }

      const target = event.target as HTMLInputElement;
      if (target.tagName !== 'INPUT' || target.type === 'checkbox') {
        return;
      }

      event.preventDefault();
      void handleLogin(event as unknown as React.FormEvent);
    };

    form.addEventListener('keydown', handleKeyDown);
    return () => form.removeEventListener('keydown', handleKeyDown);
  }, [handleLogin, state.loginInProgress]);

  function handleSubmit(event: React.FormEvent): void {
    event.preventDefault();
    handleLogin(event);
  }

  return (
    <div className="min-h-[85vh] w-full flex flex-col justify-center items-center self-center px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <form ref={formRef} className={classNames(`flex flex-col w-full max-w-md sm:max-w-lg md:w-[560px] shadow-md shadow-black/50`, {
          'pointer-events-none': state.loginInProgress,
        })} onSubmit={handleLogin}>
          <div className={`flex w-full align-middle justify-center gap-28 px-5 py-4 bg-[#9C3325]`}>
            <h2 className={`text-center text-xl font-sans text-white roboto-font`}>MEMBER LOGIN</h2>
          </div>
          <div className='bg-white w-full'>
            <div className='text-black py-6 sm:py-8'>
              {state.errorMessage && (
                <div className="w-11/12 mb-4 mx-auto bg-red-100 border border-red-400 text-red-700 p-3 rounded-md text-sm">
                  <div dangerouslySetInnerHTML={{ 
                    __html: String(state.errorMessage || '').replace(/\n/g, '<br>') 
                  }} />
                  {redirectUrl && 
                    <Link 
                      to={redirectUrl} 
                      state={{ userName: state.userName }}
                      className="text-red-700 underline">here
                    </Link>
                  }
                </div>
              )}
              {state.googleLinkMessage && (
                <div className="w-11/12 mb-4 mx-auto bg-green-100 border border-green-400 text-green-700 p-3 rounded-md text-sm">
                  {state.googleLinkMessage}
                </div>
              )}
              <div className='w-full px-4 sm:px-6 flex flex-col gap-y-2 '>
                <label htmlFor="username" className="text-sm font-normal text-gray-700">Username</label>
                <input
                  className={classNames('border w-full mb-4 px-3 py-2 h-10 mx-auto flex roboto_font text-sm', css.grayWsgInput, {
                    'border-red-500': state.errorMessage.toLowerCase().includes("a username"),
                    'border-gray-300': !state.errorMessage.toLowerCase().includes("a username")
                  })}
                  type="text"
                  value={state.userName}
                  onChange={e => dispatchLocal({ type: 'SET_USER_NAME', payload: e.target.value.replace(/\s+/g, "") })}
                />
              </div>
              <div className='w-full px-4 sm:px-6 flex flex-col gap-y-2 '>
                <label htmlFor="password" className="text-sm font-normal text-gray-700">Password</label>
                <div className="relative">
                  <input
                    className={classNames('border w-full mb-4 px-3 py-2 h-10 mx-auto flex roboto_font text-sm pr-10', css.grayWsgInput, {
                      'border-red-500': state.errorMessage.toLowerCase().includes("a password"),
                      'border-gray-300': !state.errorMessage.toLowerCase().includes("a password")
                    })}
                    type={showPassword ? "text" : "password"}
                    value={state.password}
                    onChange={e => dispatchLocal({ type: 'SET_PASSWORD', payload: e.target.value })}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-[0.5rem] p-1 flex items-center justify-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOpen /> : <EyeClosed />}
                  </button>
                </div>
              </div>
              <NewButton
                text="LOGIN"
                type="submit"
                onClick={handleSubmit}
                className={`w-11/12 text-white mx-auto flex text-center align-middle justify-center py-3 sm:py-4 inter_font text-sm sm:text-base ${css.buttonWsg}`}
              />
            </div>
            <div className='flex flex-row items-center justify-center px-4 sm:px-6'>
              <hr className='w-4/12' />
              <span className='text-[#929292] text-xs sm:text-sm px-2 whitespace-nowrap'> Single Sign-On Login:</span>
              <hr className='w-4/12' />
            </div>
            <div className='w-full flex flex-row items-center gap-x-3 justify-center mb-6 mt-4 px-4 sm:px-6'>
              <div className='flex items-center justify-center w-1/2 hover:opacity-90 transition-opacity bg-[#0077B5] h-10 cursor-pointer rounded' onClick={LoginWithLinkedIn}>
                <img 
                  src={ssolinkedin}
                  alt='linkedin'
                  className="w-5 h-5"
                />
              </div>
              <div className='flex items-center justify-center w-1/2 hover:opacity-90 transition-opacity bg-[#4285F4] h-10 cursor-pointer rounded' onClick={LoginWithGoogle}>
                <img 
                  src={ssogoogle}
                  alt='google'
                  className="w-5 h-5"
                />
              </div>
            </div>
            <div className={`mx-auto w-11/12 mb-4 align-middle flex flex-row justify-between items-center gap-3 px-4 sm:px-6 ${css.redTextWsg} roboto_font`}>
              <a href='/forgot-password' className="text-sm hover:underline">Forgot Password?</a>
              <div className={`${css.checkboxContainer}`}>
                <input className='mr-2' type='checkbox' id={`${css.remember_user}`} checked={state.checked} onChange={handleCheckboxChange} />
                <label className={`roboto_font ${css.customLabel} text-[#929292] text-sm`} htmlFor="remember_user">Remember Me</label>
              </div>
            </div>
          </div>
          
        </form>
      </div>
  );
};

export default LoginForm;
